const DataSource = require("./datasource").DataSource;

/**
 * Debounces Store events that requires an XHR to avoid client lag,
 * and server overload.
 * @param func
 * @param wait
 * @returns {Function}
 * @private
 */
function debounceStoreEvent(func, wait) {
    let timeout;
    return (eventData) => {
        const later = () => {
            timeout = null;
            func(eventData);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * @private
 */
class StoreEventListener {
    /**
     * Instantiate a StoreEventListener, should not be called by user code.
     * @since 1.3.0
     * @constructor
     */
    constructor(eventManager, source, eventName, callback) {
        /**
         * @type {StoreEventManager}
         * @private
         */
        this._storeEventManager = eventManager;
        /**
         * @type {Object}
         * @protected
         */
        this.source = source;
        /**
         * @type {String}
         * @protected
         */
        this.eventName = eventName;
        /**
         * @type {StoreEventListener}
         * @protected
         */
        this.callback = callback;
    }

    /**
     * Removes event listener.
     */
    remove() {
        this._storeEventManager.removeEventListener(this);
    }
}

/**
 * @typedef {String} EventName
 * @description EventName
 * EventName should be one of the following: `click`, `mousedown`, `mouseout`, `mouseover`, `mouseup`.
 */

/**
 * @callback StoreEventCallback
 * @description The marker parameter is only valued when the event is generated from a
 * [MarkersRenderer](#woosmap.layers.renderers.MarkersRenderer).
 * @param {Store} store the store data that generated the event.
 * @param {google.maps.Marker} [marker] the marker object associated, valued only when using
 */

/**
 * @typedef {Object} StoreEventListeners
 * @property {Array.<StoreEventCallback>} [click]
 * @property {Array.<StoreEventCallback>} [mousedown]
 * @property {Array.<StoreEventCallback>} [mouseout]
 * @property {Array.<StoreEventCallback>} [mouseover]
 * @property {Array.<StoreEventCallback>} [mouseup]
 * @private
 */

/**
 * @private
 */
class StoreEventManager {
    /**
     * StoreEventManager
     * @constructor
     * @private
     * @since 1.3.0
     */
    constructor() {
        /**
         * @type {StoreEventListeners}
         * @private
         */
        this._listeners = {};
        this._storeCache = {};

        /**
         * @type {StoreEventListeners}
         * @private
         */
        this._internalListeners = {};

        /**
         * @type {Object.<Object, StoreEventListeners>}
         * @private
         */
        this._specificListeners = {};

        /**
         * Inner DataSource used to fetch store by id.
         * @type {woosmap.DataSource}
         * @private
         */
        this._dataSource = new DataSource();

        this._debouncedHandler = debounceStoreEvent(this._handleEventWithStoreId.bind(this), 100);
    }

    /**
     * Returns supported event names.
     * @returns {Array.<String>}
     */
    supportedEvents() {
        return ["click", "mouseup", "mousedown", "mouseout", "mouseover"];
    }

    /**
     * Allow mocking internal dataSource.
     * @param {woosmap.DataSource} dataSource
     * @private
     */
    _setDataSource(dataSource) {
        this._dataSource = dataSource;
    }

    /**
     * Handles an event that needs to call getStoreById.
     * @param event
     * @private
     */
    _handleEventWithStoreId(event) {
        this._dataSource.getStoreById(
            event.storeOrId,
            function (store) {
                this._triggerAllEventListeners(event.source, event.eventName, store, event.marker);

                this._storeCache[event.storeOrId] = store;
            }.bind(this),
        );
    }

    /**
     * Adds a listener to eventListeners object passed as parameter
     * @param {StoreEventListeners} eventListeners
     * @param {EventName} eventName
     * @param {StoreEventCallback} callback
     * @private
     */
    _addListener(eventListeners, eventName, callback) {
        if (eventListeners[eventName] !== undefined) {
            eventListeners[eventName].push(callback);
        } else {
            eventListeners[eventName] = [callback];
        }
    }

    /**
     * Removes the StoreEventListener from eventListeners.
     * @param {StoreEventListeners} eventListeners
     * @param {StoreEventListener} listener
     * @private
     */
    _removeEventListenerFromObject(eventListeners, listener) {
        const eventName = listener.eventName;

        const listenerForEvent = eventListeners[eventName];

        if (listenerForEvent !== undefined) {
            const listenerIndex = listenerForEvent.indexOf(listener.callback);
            if (listenerIndex > -1) {
                delete listenerForEvent[listenerIndex];
            }
        }
    }

    /**
     * Triggers the event listeners for the specified event.
     * @param {StoreEventListeners} eventListeners
     * @param {EventName} eventName
     * @param {Store} store
     * @param {google.maps.Marker} [marker]
     * @private
     */
    _triggerEventListeners(eventListeners, eventName, store, marker) {
        const eventListenerChain = eventListeners[eventName];
        if (eventListenerChain !== undefined) {
            eventListenerChain.forEach((listener) => {
                if (listener !== undefined && typeof listener === "function") {
                    listener(store, marker);
                }
            });
        }
    }

    /**
     * Triggers all events for eventName, handles user defined listeners failures.
     * @param {Object} source
     * @param {EventName} eventName
     * @param {Store} store
     * @param {google.maps.Marker} [marker]
     * @private
     */
    _triggerAllEventListeners(source, eventName, store, marker) {
        try {
            this._triggerEventListeners(this._listeners, eventName, store, marker);

            if (source !== undefined) {
                const specificListenersForSource = this._specificListeners[source];
                if (specificListenersForSource !== undefined) {
                    this._triggerEventListeners(specificListenersForSource, eventName, store, marker);
                }
            }
        } catch (e) {
            if (console && typeof console.error === "function") {
                console.error(e);
            }
        } finally {
            this._triggerEventListeners(this._internalListeners, eventName, store, marker);
        }
    }

    /**
     * Triggers listeners registered for eventName.
     * When called with a storeId, fetches the store data with datasource#getStoreById.
     * @param {Object} source
     * @param {EventName} eventName
     * @param {Store|String} storeOrId
     * @param {google.maps.Marker} [marker]
     * @private
     */
    _trigger(source, eventName, storeOrId, marker) {
        if (this._hasListenerDefinedForEvent(source, eventName)) {
            if (typeof storeOrId === "string") {
                if (this._storeCache[storeOrId]) {
                    this._triggerAllEventListeners(source, eventName, this._storeCache[storeOrId], marker);
                } else {
                    this._debouncedHandler({
                        source: source,
                        eventName: eventName,
                        storeOrId: storeOrId,
                        marker: marker,
                    });
                }
            } else {
                this._triggerAllEventListeners(source, eventName, storeOrId, marker);
            }
        }
    }

    /**
     * Checks if a listener for eventName has been registered.
     * @param {Object} source
     * @param {EventName} eventName
     * @private
     */
    _hasListenerDefinedForEvent(source, eventName) {
        const specificListenersForSource = this._specificListeners[source] || {};
        return (
            this._listeners[eventName] || specificListenersForSource[eventName] || this._internalListeners[eventName]
        );
    }

    /**
     * Adds a listener for event.
     * The listeners are user defined.
     * @param {EventName} eventName
     * @param {StoreEventCallback} callback
     * @returns {StoreEventListener}
     */
    addListener(eventName, callback) {
        this._addListener(this._listeners, eventName, callback);
        return new StoreEventListener(this, undefined, eventName, callback);
    }

    /**
     * Adds an internal event listener.
     * The internal event listeners are used for events that are needed for views (mostly click to select a marker).
     * @param {EventName} eventName
     * @param {StoreEventCallback} callback
     * @return {StoreEventListener}
     * @protected
     */
    addInternalListener(eventName, callback) {
        return this._addListener(this._internalListeners, eventName, callback);
    }

    /**
     * Adds a listener specific to an event source.
     * This is for keeping compatibility with older event system.
     * @param {Object} source
     * @param {EventName} eventName
     * @param {StoreEventCallback} callback
     * @returns {StoreEventListener}
     */
    addListenerForSource(source, eventName, callback) {
        this.clearListenersWithSource(source, eventName);
        const specificListenersForSource = this._specificListeners[source] || {};
        this._addListener(specificListenersForSource, eventName, callback);
        this._specificListeners[source] = specificListenersForSource;
        return new StoreEventListener(this, source, eventName, callback);
    }

    /**
     * Triggers an event for the eventName with store and marker if available.
     * @param {EventName} eventName
     * @param {Store|string} storeOrId
     * @param {google.maps.Marker} [marker]
     */
    trigger(eventName, storeOrId, marker) {
        this._trigger(undefined, eventName, storeOrId, marker);
    }

    /**
     * Triggers listeners, internalListeners and source specific listeners.
     * @param {Object} source
     * @param {EventName} eventName
     * @param {Store|String} storeOrId
     * @param {google.maps.Marker} [marker]
     */
    triggerWithSource(source, eventName, storeOrId, marker) {
        this._trigger(source, eventName, storeOrId, marker);
    }

    /**
     * Clears the listeners specific to source for eventName.
     * @param {Object} source
     * @param {EventName} eventName
     */
    clearListenersWithSource(source, eventName) {
        const specificListenersForSource = this._specificListeners[source];
        if (specificListenersForSource !== undefined) {
            if (specificListenersForSource[eventName] !== undefined) {
                delete specificListenersForSource[eventName];
            }
        }
    }

    /**
     * Removes the specified listener
     * @param {StoreEventListener} listener
     */
    removeEventListener(listener) {
        if (listener.source) {
            this._removeEventListenerFromObject(this._specificListeners[listener.source], listener);
        } else {
            this._removeEventListenerFromObject(this._listeners, listener);
        }
    }
}

module.exports = StoreEventManager;
